import React, { useState, useRef, useEffect, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    Box,
    Stepper,
    Step,
    StepLabel,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Autocomplete,
    Switch,
    FormControlLabel,
    Tabs,
    Tab,
    Paper,
    Select,
    FormControl,
    InputLabel,
    MenuItem,
    ListSubheader
} from '@mui/material';
import {
    Add,
    Delete,
    Edit,
    LocationOn,
    Event,
    Search,
    Upload,
    Check,
    Warning,
    CalendarToday,
    Place
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';
import { GoogleMapsContext } from '../constants/GoogleMapsProvider';
import googlePlacesService from '../services/GooglePlacesService';
import { EventTypes, EventType, EventTypeGroup } from '../constants/common';
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, getHeaders, dataConnect } from '../firebaseConfig';
import { createTourLocation, tourLocationSearch } from "@firebasegen/default-connector";

const TourCreationModal = ({
    open,
    onClose,
    onCreateTour,
    artist,
    loading = false
}) => {
    const artistId = artist?.id;
    const { isLoaded: isGoogleMapsLoaded } = useContext(GoogleMapsContext);
    const [activeStep, setActiveStep] = useState(0);
    const [tourData, setTourData] = useState({
        name: '',
        description: '',
        startDate: null,
        endDate: null,
        status: 'PLANNED'
    });

    const [inputMode, setInputMode] = useState(0); // 0: Manual, 1: Bulk Text
    const [bulkInput, setBulkInput] = useState('');
    const [parsedEvents, setParsedEvents] = useState([]);
    const [venueSearchResults, setVenueSearchResults] = useState({});
    const [manualVenueSearchResults, setManualVenueSearchResults] = useState({});
    const [venueSearchLoading, setVenueSearchLoading] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [placesServiceReady, setPlacesServiceReady] = useState(false);

    const steps = ['Tour Details', 'Add Events', 'Review & Create'];

    // Example bulk input for user guidance
    const exampleBulkInput = `Dato,City,Country,Venue,Status,Notes
15.01.2025,Oslo,Norway,Spektrum,Confirmed,
16.01.2025,Bergen,Norway,Bergen Live,Confirmed,
17.01.2025,Stavanger,Norway,Stavanger Konserthus,Confirmed,Evening show
18.01.2025,Travel Day,,,,
19.01.2025,Hamburg,Germany,Docks,Confirmed,Opening for main act`;

    // Initialize Google Places service when Google Maps API is loaded
    useEffect(() => {
        if (isGoogleMapsLoaded && !placesServiceReady) {
            console.log('Google Maps loaded, attempting to initialize Places service...');
            try {
                googlePlacesService.initialize();
                setPlacesServiceReady(true);
                console.log('Google Places service initialized successfully');
            } catch (error) {
                console.error('Failed to initialize Google Places service:', error);
                console.warn('Venue search will be disabled. Check your Google Maps API key and ensure Places API is enabled.');
                setPlacesServiceReady(false);

                // Show user-friendly message about Places API issues
                if (error.message.includes('Places library not loaded')) {
                    console.error('Places library not loaded. Make sure Places is included in the libraries array.');
                } else if (error.message.includes('API key') || error.message.includes('billing')) {
                    console.error('API key or billing issue. Check Google Cloud Console for Places API configuration.');
                }
            }
        }
    }, [isGoogleMapsLoaded, placesServiceReady]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!open) {
            setActiveStep(0);
            setTourData({
                name: '',
                description: '',
                startDate: null,
                endDate: null,
                status: 'PLANNED'
            });
            setBulkInput('');
            setParsedEvents([]);
            setVenueSearchResults({});
            setManualVenueSearchResults({});
            setVenueSearchLoading({});
            setValidationErrors([]);
        }
    }, [open]);

    // Parse bulk input text using AI
    const parseBulkInput = async () => {
        setIsProcessing(true);
        setValidationErrors([]);

        try {
            const functions = getFunctions(app);
            const importEventsFromText = httpsCallable(functions, "importEventsFromText");

            const result = await importEventsFromText({
                text: bulkInput,
                artistId: artistId
            });

            if (!result.data.success) {
                setValidationErrors([result.data.message || "Failed to process text"]);
                return;
            }

            const extractedEvents = result.data.events;

            if (extractedEvents.length === 0) {
                setValidationErrors(["No events could be extracted from the text"]);
                return;
            }

            // Convert extracted events to the format expected by the modal
            const events = extractedEvents.map((event, i) => ({
                id: `temp-${i}`,
                date: moment(event.date).toDate(),
                artistId: artistId,
                venueName: event.venueName,
                location: event.city,
                country: event.country,
                ticketInfo: event.notes || '',
                ticketLink: event.link || '', // Map ticket link
                fullLocationString: `${event.venueName}, ${event.city} (${event.country})`,
                venueSearchQuery: `${event.venueName} ${event.city} ${event.country}`.trim(),
                venueSelected: null,
                eventType: detectEventType(event.venueName, event.city),
                status: 'parsed',
                confidence: event.confidence,
                name: event.name,
                description: event.description
            }));

            setValidationErrors([]);
            setParsedEvents(events);
            setInputMode(0); // Switch to manual list view to show parsed events

            // Auto-search Google Places for each venue
            if (events.length > 0) {
                searchVenuesForEvents(events);
            }

        } catch (error) {
            console.error("AI parsing error:", error);
            setValidationErrors(['Failed to parse input with AI: ' + (error.message || 'Unknown error')]);
        } finally {
            setIsProcessing(false);
        }
    };

    // Search Google Places for venues
    // Search Google Places for venues
    const searchVenuesForEvents = async (events) => {
        const results = {};
        const bestMatches = {}; // Store best match for each event ID

        for (const event of events) {
            let combinedResults = [];

            // 1. Search Local Database First
            try {
                console.log(`Searching DB for venue: ${event.venueName}`);
                const { data } = await tourLocationSearch(dataConnect, { s: event.venueName });

                if (data?.venues?.length > 0) {
                    console.log(`Found ${data.venues.length} venues in DB for ${event.venueName}`);

                    // Deduplicate DB results based on googleId or name
                    const uniqueDbVenues = new Map();
                    data.venues.forEach(v => {
                        const key = v.googleId || v.name;
                        if (!uniqueDbVenues.has(key)) {
                            uniqueDbVenues.set(key, {
                                ...v,
                                place_id: v.id, // Use DB ID as place_id equivalent
                                formatted_address: v.formattedAddress || `${v.address || ''}, ${v.city || ''}, ${v.country || ''}`.replace(/^, |, $/g, ''),
                                source: 'db',
                                rating: null,
                                user_ratings_total: null
                            });
                        }
                    });

                    combinedResults = Array.from(uniqueDbVenues.values());
                }
            } catch (error) {
                console.error(`Failed to search DB for ${event.venueName}:`, error);
            }

            // 2. If not found in DB, search Google Places
            // We search Google if we have no DB results OR if we want to enrich/verify
            // For now, let's search Google if we have < 1 confident result or just to offer more options
            if (combinedResults.length === 0) {
                // Check if Google Places service is ready
                if (!placesServiceReady || !googlePlacesService.isReady()) {
                    console.warn('Google Places service not ready, using fallback');
                    results[event.id] = combinedResults; // Return whatever we have
                } else {
                    try {
                        console.log(`Searching Google for venue: ${event.venueSearchQuery}`);
                        const searchResults = await googlePlacesService.searchVenues(event.venueSearchQuery);
                        results[event.id] = searchResults;
                        console.log(`Found ${searchResults.length} results from Google for ${event.venueSearchQuery}`);
                        combinedResults = [...combinedResults, ...searchResults];
                    } catch (error) {
                        console.error(`Failed to search for ${event.venueSearchQuery}:`, error);
                    }
                }
            }

            results[event.id] = combinedResults;

            // Auto-select the first result if available
            if (combinedResults.length > 0) {
                bestMatches[event.id] = combinedResults[0];
            }
        }

        setVenueSearchResults(results);

        // Batch update parsedEvents with selected venues
        setParsedEvents(prev => prev.map(event => {
            if (bestMatches[event.id] && !event.venueSelected) {
                const venue = bestMatches[event.id];
                return {
                    ...event,
                    venueName: venue.name,
                    location: venue.vicinity || venue.formatted_address || event.location,
                    venueSelected: venue,
                    status: 'venue_selected' // This confirms we have a linked venue
                };
            }
            return event;
        }));
    };

    // Handle venue selection for an event
    const handleVenueSelect = (eventId, venue) => {
        setParsedEvents(prev => prev.map(event =>
            event.id === eventId
                ? { ...event, venueSelected: venue, status: 'venue_selected' }
                : event
        ));
    };

    // Search venues for manual event entry
    const searchVenueForManualEvent = async (eventId, query) => {
        if (!query || query.length < 3) {
            setManualVenueSearchResults(prev => ({ ...prev, [eventId]: [] }));
            return;
        }

        if (!placesServiceReady || !googlePlacesService.isReady()) {
            console.warn('Google Places service not ready for manual search');
            setManualVenueSearchResults(prev => ({ ...prev, [eventId]: [] }));
            return;
        }

        setVenueSearchLoading(prev => ({ ...prev, [eventId]: true }));

        try {
            console.log(`Searching venues for manual entry: ${query}`);
            const searchResults = await googlePlacesService.searchVenues(query);

            // Validate search results
            const validResults = Array.isArray(searchResults) ? searchResults : [];
            setManualVenueSearchResults(prev => ({ ...prev, [eventId]: validResults }));
            console.log(`Found ${validResults.length} venues for manual entry: ${query}`);
        } catch (error) {
            console.error(`Failed to search venues for manual entry "${query}":`, error);
            // Set empty results on error
            setManualVenueSearchResults(prev => ({ ...prev, [eventId]: [] }));

            // Optionally show user-friendly error message
            if (error.message.includes('OVER_QUERY_LIMIT')) {
                console.warn('Google Places API quota exceeded');
            } else if (error.message.includes('REQUEST_DENIED')) {
                console.warn('Google Places API request denied - check API key');
            }
        } finally {
            setVenueSearchLoading(prev => ({ ...prev, [eventId]: false }));
        }
    };

    // Handle venue selection for manual event
    const handleManualVenueSelect = (eventId, venue) => {
        if (venue) {
            // Auto-create activities if valid ticket link source is found
            // This assumes if we have a website/url from the venue, we might want to add Doors/Show placeholders
            const hasTicketLink = venue.website || venue.url || (venue.source === 'db' && venue.website);
            let additionalActivities = [];

            if (hasTicketLink) {
                console.log("Ticket/Website link found, adding default activities");
                additionalActivities = [
                    { name: "Doors", type: "OTHER", notes: "Auto-created from venue info" },
                    { name: "Show", type: "CONCERT", notes: "Auto-created from venue info" }
                ];
            }

            // Update event with selected venue data
            setParsedEvents(prev => prev.map(event =>
                event.id === eventId
                    ? {
                        ...event,
                        venueName: venue.name,
                        location: venue.vicinity || venue.formatted_address,
                        venueSelected: venue,
                        status: 'venue_selected',
                        // Append activities if we found a link, preserving existing ones if any
                        activities: [...(event.activities || []), ...additionalActivities]
                    }
                    : event
            ));
        }
    };

    // Manual event addition
    const addManualEvent = () => {
        // Determine the default date for the new event
        let defaultDate = new Date(); // Fallback to today

        if (tourData.startDate) {
            // If tour start date is set, use that as the base
            defaultDate = new Date(tourData.startDate);

            // If there are existing events, set the new event to the day after the last event
            if (parsedEvents.length > 0) {
                const lastEventDate = new Date(Math.max(...parsedEvents.map(e => new Date(e.date))));
                defaultDate = new Date(lastEventDate);
                defaultDate.setDate(defaultDate.getDate() + 1); // Add 1 day
            }
        } else if (parsedEvents.length > 0) {
            // If no tour start date but there are events, use the day after the last event
            const lastEventDate = new Date(Math.max(...parsedEvents.map(e => new Date(e.date))));
            defaultDate = new Date(lastEventDate);
            defaultDate.setDate(defaultDate.getDate() + 1); // Add 1 day
        }

        const newEvent = {
            id: `manual-${Date.now()}`,
            date: defaultDate,
            venueName: '',
            location: '',
            country: '',
            ticketInfo: '',
            venueSelected: null,
            eventType: EventType.CONCERT, // Default to concert for manual entry
            status: 'manual'
        };
        setParsedEvents(prev => [...prev, newEvent]);
    };

    // Remove event
    const removeEvent = (eventId) => {
        setParsedEvents(prev => prev.filter(event => event.id !== eventId));
    };

    // Update manual event
    const updateManualEvent = (eventId, field, value) => {
        setParsedEvents(prev => prev.map(event =>
            event.id === eventId
                ? { ...event, [field]: value }
                : event
        ));
    };

    // Validate and create tour
    const handleCreateTour = async () => {
        const errors = [];

        // Validate tour data
        if (!tourData.name.trim()) {
            errors.push('Tour name is required');
        }

        parsedEvents.forEach((event, index) => {
            if (!event.date) {
                errors.push(`Event ${index + 1}: Date is required`);
            }
            if (!event.venueName.trim()) {
                errors.push(`Event ${index + 1}: Venue name is required`);
            }
            if (!event.location.trim()) {
                errors.push(`Event ${index + 1}: Location is required`);
            }
        });

        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Calculate tour dates from events if any exist
        let calculatedStartDate = null;
        let calculatedEndDate = null;
        if (parsedEvents.length > 0) {
            const eventDates = parsedEvents.map(e => moment(e.date));
            calculatedStartDate = moment.min(eventDates).toDate();
            calculatedEndDate = moment.max(eventDates).toDate();
        }

        const tourToCreate = {
            ...tourData,
            artistId, // Explicitly add artistId from props
            startDate: tourData.startDate || calculatedStartDate,
            endDate: tourData.endDate || calculatedEndDate,
            events: parsedEvents
        };

        await onCreateTour(tourToCreate);
    };

    // Step navigation
    const handleNext = () => {
        if (activeStep === 1 && inputMode === 1 && parsedEvents.length === 0) {
            parseBulkInput();
        }
        setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
    };

    const handleBack = () => {
        setActiveStep(prev => Math.max(prev - 1, 0));
    };

    const canProceed = () => {
        switch (activeStep) {
            case 0:
                return tourData.name.trim().length > 0;
            case 1:
                return true; // Events are optional
            case 2:
                return validationErrors.length === 0;
            default:
                return false;
        }
    };

    const getEventStatusColor = (status) => {
        switch (status) {
            case 'parsed': return 'info';
            case 'venue_selected': return 'success';
            case 'manual': return 'warning';
            default: return 'default';
        }
    };

    const getEventTypeColor = (eventType) => {
        switch (eventType) {
            case EventType.TRAVEL_DAY: return 'info';
            case EventType.OFF_DAY: return 'secondary';
            case EventType.REHEARSAL_DAY: return 'warning';
            case EventType.PRESS_DAY: return 'info';
            case EventType.PHOTO_SHOOT: return 'secondary';
            case EventType.CONCERT:
            case EventType.FESTIVAL: return 'primary';
            default: return 'default';
        }
    };

    // Smart event type detection from venue name
    const detectEventType = (venueName, location) => {
        const text = `${venueName} ${location}`.toLowerCase();

        // Travel day indicators
        if (text.includes('travel') || text.includes('transfer') || text.includes('flight') ||
            text.includes('airport') || text.includes('train') || text.includes('bus')) {
            return EventType.TRAVEL_DAY;
        }

        // Off day indicators
        if (text.includes('off day') || text.includes('rest day') || text.includes('free day') ||
            text.includes('hotel') && !text.includes('venue')) {
            return EventType.OFF_DAY;
        }

        // Rehearsal indicators
        if (text.includes('rehearsal') || text.includes('soundcheck') || text.includes('tech')) {
            return EventType.REHEARSAL_DAY;
        }

        // Press/media indicators
        if (text.includes('interview') || text.includes('press') || text.includes('media') ||
            text.includes('radio') || text.includes('tv')) {
            return EventType.PRESS_DAY;
        }

        // Photo shoot indicators
        if (text.includes('photo') || text.includes('shoot') || text.includes('filming')) {
            return EventType.PHOTO_SHOOT;
        }

        // Festival indicators
        if (text.includes('festival') || text.includes('fest') || text.includes('open air')) {
            return EventType.FESTIVAL;
        }

        // Default to concert
        return EventType.CONCERT;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '80vh' }
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5">Create New Tour</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Artist: {artist?.name}
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {/* Step 1: Tour Details */}
                    {activeStep === 0 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Tour Name"
                                    placeholder="e.g., European Tour 2026"
                                    value={tourData.name}
                                    onChange={(e) => setTourData(prev => ({ ...prev, name: e.target.value }))}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    placeholder="Tour description..."
                                    multiline
                                    rows={3}
                                    value={tourData.description}
                                    onChange={(e) => setTourData(prev => ({ ...prev, description: e.target.value }))}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker
                                    label="Start Date (Optional)"
                                    value={tourData.startDate}
                                    onChange={(date) => setTourData(prev => ({ ...prev, startDate: date }))}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker
                                    label="End Date (Optional)"
                                    value={tourData.endDate}
                                    onChange={(date) => setTourData(prev => ({ ...prev, endDate: date }))}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    Start and end dates will be automatically calculated from your events if not provided.
                                </Alert>
                            </Grid>
                        </Grid>
                    )}

                    {/* Step 2: Add Events */}
                    {activeStep === 1 && (
                        <Box>
                            <Tabs value={inputMode} onChange={(e, newValue) => setInputMode(newValue)} sx={{ mb: 3 }}>
                                <Tab label="Manual Entry" icon={<Edit />} />
                                <Tab label="AI Bulk Import" icon={<Upload />} />
                            </Tabs>

                            {inputMode === 0 && (
                                <Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6">Manual Event Entry</Typography>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Add />}
                                            onClick={addManualEvent}
                                        >
                                            Add Event
                                        </Button>
                                    </Box>

                                    {/* Google Places Service Status */}
                                    {isGoogleMapsLoaded && !placesServiceReady && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            Google Places service is initializing. Venue search will be available shortly.
                                        </Alert>
                                    )}

                                    {!isGoogleMapsLoaded && (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            Loading Google Maps API for venue search...
                                        </Alert>
                                    )}

                                    {isGoogleMapsLoaded && placesServiceReady && (
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            ✓ Google Places venue search is ready
                                        </Alert>
                                    )}

                                    {parsedEvents.length === 0 ? (
                                        <Alert severity="info">
                                            No events added yet. Click "Add Event" to start adding events manually.
                                        </Alert>
                                    ) : (
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Date</TableCell>
                                                    <TableCell>Event Type</TableCell>
                                                    <TableCell>Venue</TableCell>
                                                    <TableCell>Location</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell align="right">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {parsedEvents.map((event) => (
                                                    <TableRow key={event.id}>
                                                        <TableCell>
                                                            <DatePicker
                                                                value={event.date}
                                                                onChange={(date) => updateManualEvent(event.id, 'date', date)}
                                                                renderInput={(params) => <TextField {...params} size="small" />}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                                                <Select
                                                                    value={event.eventType || EventType.CONCERT}
                                                                    onChange={(e) => updateManualEvent(event.id, 'eventType', e.target.value)}
                                                                    displayEmpty
                                                                >
                                                                    {Object.entries(
                                                                        EventTypes.reduce((acc, type) => {
                                                                            const group = type.group || 'OTHER';
                                                                            if (!acc[group]) acc[group] = [];
                                                                            acc[group].push(type);
                                                                            return acc;
                                                                        }, {})
                                                                    ).map(([group, types]) => [
                                                                        <ListSubheader key={`header-${group}`} sx={{ fontWeight: 'bold', lineHeight: '36px', bgcolor: 'background.paper' }}>
                                                                            {EventTypeGroup[group]?.name || 'Other'}
                                                                        </ListSubheader>,
                                                                        ...types.map((type) => (
                                                                            <MenuItem key={type.value} value={type.value} sx={{ pl: 4 }}>
                                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                                    {type.icon}
                                                                                    <Typography variant="body2">{type.name}</Typography>
                                                                                </Box>
                                                                            </MenuItem>
                                                                        ))
                                                                    ])}
                                                                </Select>
                                                            </FormControl>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Autocomplete
                                                                size="small"
                                                                options={manualVenueSearchResults[event.id] || venueSearchResults[event.id] || []}
                                                                getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                                                                loading={venueSearchLoading[event.id] || false}
                                                                filterOptions={(x) => x} // Disable local filtering
                                                                renderOption={(props, option) => (
                                                                    <li {...props}>
                                                                        <Box sx={{ width: '100%' }}>
                                                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                                <Typography variant="body2" fontWeight="bold">
                                                                                    {option.name}
                                                                                </Typography>
                                                                                {option.rating && (
                                                                                    <Chip
                                                                                        label={`⭐ ${option.rating.toFixed(1)}`}
                                                                                        size="small"
                                                                                        color="primary"
                                                                                        variant="outlined"
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                                {option.formatted_address}
                                                                            </Typography>
                                                                            {option.types && option.types.length > 0 && (
                                                                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                                    {option.types.slice(0, 2).join(', ')}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </li>
                                                                )}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        placeholder="Search venues..."
                                                                        size="small"
                                                                        InputProps={{
                                                                            ...params.InputProps,
                                                                            endAdornment: (
                                                                                <>
                                                                                    {venueSearchLoading[event.id] ? <CircularProgress color="inherit" size={20} /> : null}
                                                                                    {params.InputProps.endAdornment}
                                                                                </>
                                                                            ),
                                                                        }}
                                                                    />
                                                                )}
                                                                onInputChange={(e, value) => {
                                                                    if (value && value.length >= 3) {
                                                                        searchVenueForManualEvent(event.id, value).catch(error => {
                                                                            console.error('Venue search failed:', error);
                                                                        });
                                                                    }
                                                                    // Also update the venue name for typing
                                                                    updateManualEvent(event.id, 'venueName', value);
                                                                }}
                                                                onChange={(e, value) => handleManualVenueSelect(event.id, value)}
                                                                value={event.venueSelected || event.venueName}
                                                                freeSolo
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <TextField
                                                                size="small"
                                                                placeholder="City, Country"
                                                                value={event.location}
                                                                onChange={(e) => updateManualEvent(event.id, 'location', e.target.value)}
                                                                helperText={event.venueSelected ? "Auto-filled from venue" : ""}
                                                                InputProps={{
                                                                    style: {
                                                                        backgroundColor: event.venueSelected ? '#f5f5f5' : 'transparent',
                                                                        color: event.venueSelected ? '#666' : 'inherit'
                                                                    }
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={event.status}
                                                                color={getEventStatusColor(event.status)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => removeEvent(event.id)}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Box>
                            )}

                            {inputMode === 1 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        AI-Powered Bulk Event Import
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        Paste your concert schedule in CSV format. AI will automatically extract dates, venues, and locations.
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        ✨ <strong>AI Analysis:</strong> Supports various formats including CSV headers like "Dato,City,Country,Venue,Status,Notes"
                                    </Typography>

                                    {/* Google Places Service Status */}
                                    {isGoogleMapsLoaded && !placesServiceReady && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            Google Places service is initializing. Venue search will be available shortly.
                                        </Alert>
                                    )}

                                    {!isGoogleMapsLoaded && (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            Loading Google Maps API for venue search...
                                        </Alert>
                                    )}

                                    {isGoogleMapsLoaded && placesServiceReady && (
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            ✓ Google Places venue search is ready
                                        </Alert>
                                    )}

                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={8}
                                        placeholder={exampleBulkInput}
                                        value={bulkInput}
                                        onChange={(e) => setBulkInput(e.target.value)}
                                        sx={{ mb: 2 }}
                                    />

                                    <Box display="flex" gap={2} mb={2}>
                                        <Button
                                            variant="contained"
                                            startIcon={<Search />}
                                            onClick={parseBulkInput}
                                            disabled={!bulkInput.trim() || isProcessing}
                                        >
                                            {isProcessing ? 'Parsing...' : 'Parse Events'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => setBulkInput(exampleBulkInput)}
                                        >
                                            Load Example
                                        </Button>
                                    </Box>

                                    {validationErrors.length > 0 && (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Parsing Errors:
                                            </Typography>
                                            {validationErrors.map((error, index) => (
                                                <Typography key={index} variant="body2">
                                                    • {error}
                                                </Typography>
                                            ))}
                                        </Alert>
                                    )}

                                    {isProcessing && (
                                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                                            <CircularProgress size={20} />
                                            <Typography>Processing events and searching venues...</Typography>
                                        </Box>
                                    )}

                                    {parsedEvents.length > 0 && (
                                        <Box>
                                            <Typography variant="h6" gutterBottom>
                                                Parsed Events ({parsedEvents.length})
                                            </Typography>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell>Event Type</TableCell>
                                                        <TableCell>Venue</TableCell>
                                                        <TableCell>Location</TableCell>
                                                        <TableCell>Google Places</TableCell>
                                                        <TableCell>Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {parsedEvents.map((event) => (
                                                        <TableRow key={event.id}>
                                                            <TableCell>
                                                                {moment(event.date).format('DD.MM.YYYY')}
                                                            </TableCell>
                                                            <TableCell>
                                                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                                                    <Select
                                                                        value={event.eventType || EventType.CONCERT}
                                                                        onChange={(e) => updateManualEvent(event.id, 'eventType', e.target.value)}
                                                                        displayEmpty
                                                                    >
                                                                        {Object.entries(
                                                                            EventTypes.reduce((acc, type) => {
                                                                                const group = type.group || 'OTHER';
                                                                                if (!acc[group]) acc[group] = [];
                                                                                acc[group].push(type);
                                                                                return acc;
                                                                            }, {})
                                                                        ).map(([group, types]) => [
                                                                            <ListSubheader key={`header-${group}`} sx={{ fontWeight: 'bold', lineHeight: '36px', bgcolor: 'background.paper' }}>
                                                                                {EventTypeGroup[group]?.name || 'Other'}
                                                                            </ListSubheader>,
                                                                            ...types.map((type) => (
                                                                                <MenuItem key={type.value} value={type.value} sx={{ pl: 4 }}>
                                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                                        {type.icon}
                                                                                        <Typography variant="body2">{type.name}</Typography>
                                                                                    </Box>
                                                                                </MenuItem>
                                                                            ))
                                                                        ])}
                                                                    </Select>
                                                                </FormControl>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography fontWeight="bold">
                                                                    {event.venueName}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {event.location} {event.country && `(${event.country})`}
                                                            </TableCell>
                                                            <TableCell>
                                                                {venueSearchResults[event.id] ? (
                                                                    venueSearchResults[event.id].length > 0 ? (
                                                                        <Autocomplete
                                                                            size="small"
                                                                            options={venueSearchResults[event.id] || []}
                                                                            getOptionLabel={(option) => option.name}
                                                                            renderOption={(props, option) => (
                                                                                <li {...props}>
                                                                                    <Box sx={{ width: '100%' }}>
                                                                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                                            <Typography variant="body2" fontWeight="bold">
                                                                                                {option.name}
                                                                                            </Typography>
                                                                                            {option.rating && (
                                                                                                <Chip
                                                                                                    label={`⭐ ${option.rating.toFixed(1)}`}
                                                                                                    size="small"
                                                                                                    color="primary"
                                                                                                    variant="outlined"
                                                                                                />
                                                                                            )}
                                                                                        </Box>
                                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                                            {option.formatted_address}
                                                                                        </Typography>
                                                                                        {option.types && option.types.length > 0 && (
                                                                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                                                {option.types.slice(0, 2).join(', ')}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Box>
                                                                                </li>
                                                                            )}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    placeholder="Select venue"
                                                                                    size="small"
                                                                                />
                                                                            )}
                                                                            onChange={(e, value) => handleVenueSelect(event.id, value)}
                                                                            value={event.venueSelected}
                                                                        />
                                                                    ) : (
                                                                        <Typography variant="caption" color="error">
                                                                            No venues found. Try manual entry.
                                                                        </Typography>
                                                                    )
                                                                ) : (
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                        <CircularProgress size={16} />
                                                                        <Typography variant="caption">
                                                                            {placesServiceReady ? 'Searching...' : 'Waiting for Maps API...'}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={event.status}
                                                                    color={getEventStatusColor(event.status)}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Step 3: Review & Create */}
                    {activeStep === 2 && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Review Tour Details
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Tour Information
                                            </Typography>
                                            <Box mb={1}>
                                                <Typography variant="body2" color="text.secondary">Name:</Typography>
                                                <Typography variant="body1" fontWeight="bold">{tourData.name}</Typography>
                                            </Box>
                                            <Box mb={1}>
                                                <Typography variant="body2" color="text.secondary">Artist:</Typography>
                                                <Typography variant="body1">{artist?.name}</Typography>
                                            </Box>
                                            <Box mb={1}>
                                                <Typography variant="body2" color="text.secondary">Events:</Typography>
                                                <Typography variant="body1">{parsedEvents.length} events</Typography>
                                            </Box>
                                            {tourData.description && (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">Description:</Typography>
                                                    <Typography variant="body1">{tourData.description}</Typography>
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                Tour Statistics
                                            </Typography>
                                            <Box mb={1}>
                                                <Typography variant="body2" color="text.secondary">Total Events:</Typography>
                                                <Typography variant="body1" fontWeight="bold">{parsedEvents.length}</Typography>
                                            </Box>
                                            <Box mb={1}>
                                                <Typography variant="body2" color="text.secondary">Countries:</Typography>
                                                <Typography variant="body1">
                                                    {new Set(parsedEvents.map(e => e.country).filter(Boolean)).size}
                                                </Typography>
                                            </Box>
                                            <Box mb={1}>
                                                <Typography variant="body2" color="text.secondary">Date Range:</Typography>
                                                <Typography variant="body1">
                                                    {parsedEvents.length > 0 ? (
                                                        `${moment.min(parsedEvents.map(e => moment(e.date))).format('DD.MM.YYYY')} - ${moment.max(parsedEvents.map(e => moment(e.date))).format('DD.MM.YYYY')}`
                                                    ) : 'No events'}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Event List
                                    </Typography>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Event Type</TableCell>
                                                <TableCell>Venue</TableCell>
                                                <TableCell>Location</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {parsedEvents.map((event) => (
                                                <TableRow key={event.id}>
                                                    <TableCell>
                                                        {moment(event.date).format('DD.MM.YYYY')}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={EventTypes.find(type => type.value === (event.eventType || EventType.CONCERT))?.name || 'Concert'}
                                                            icon={EventTypes.find(type => type.value === (event.eventType || EventType.CONCERT))?.icon}
                                                            size="small"
                                                            color={getEventTypeColor(event.eventType || EventType.CONCERT)}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography fontWeight="bold">
                                                            {event.venueName}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {event.location} {event.country && `(${event.country})`}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={event.venueSelected ? 'Ready' : 'Venue needed'}
                                                            color={event.venueSelected ? 'success' : 'warning'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Grid>

                                {validationErrors.length > 0 && (
                                    <Grid item xs={12}>
                                        <Alert severity="error">
                                            <Typography variant="subtitle2" gutterBottom>
                                                Please fix these issues before creating the tour:
                                            </Typography>
                                            {validationErrors.map((error, index) => (
                                                <Typography key={index} variant="body2">
                                                    • {error}
                                                </Typography>
                                            ))}
                                        </Alert>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>

                    {activeStep > 0 && (
                        <Button onClick={handleBack} disabled={loading}>
                            Back
                        </Button>
                    )}

                    {activeStep < steps.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!canProceed() || loading}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleCreateTour}
                            disabled={!canProceed() || loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <Check />}
                        >
                            {loading ? 'Creating...' : 'Create Tour'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default TourCreationModal;